import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ReactComponent as ExportIcon } from '../../assets/icons/export.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as KeyIcon } from '../../assets/icons/key.svg';
import { ReactComponent as UserEditIcon } from '../../assets/icons/user-edit.svg';
import { ReactComponent as CrossIcon } from '../../assets/icons/cross.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete.svg';
import SearchBar from '../SearchBar/SearchBar';
import TableauDeBord from '../TableauDeBord/TableauDeBord';
import Button from '../Button/Button';
import CreationComptesModal from '../CreationComptesModal/CreationComptesModal';
import EditUserModal from '../EditUserModal/EditUserModal';
import PassationModal from '../PassationModal/PassationModal';
import DeleteMemberModal, { DeleteMemberInfo } from '../DeleteMemberModal/DeleteMemberModal';
import {
  convertApiRoleToUiRole,
  convertUiRoleToApiRole,
  isClientRole,
  isPrestataireRole,
  isPrestataireLibreRole,
  isAdminRole
} from '../../utils/roleUtils';
import './GestionComptes.css';
import './menu-actions.css'; // Ajout du style spécifique pour les menus

const formatPhone = (phone?: string | null): string => {
  if (!phone) return '-';
  const digits = phone.replace(/\D/g, '');
  let n = digits;
  if (digits.startsWith('33') && digits.length === 11) n = digits.slice(2);
  else if (digits.startsWith('0') && digits.length === 10) n = digits.slice(1);
  else return phone;
  return `+33 ${n[0]} ${n.slice(1, 3)} ${n.slice(3, 5)} ${n.slice(5, 7)} ${n.slice(7, 9)}`;
};

const formatEmail = (email?: string | null): string => email ? email.toLowerCase() : '-';

// Basculer entre 'cards' et 'table' pour la vue membres acheteurs
const ACHETEURS_VIEW_MODE: 'cards' | 'table' = 'cards';

const ROLE_DISPLAY_RANK: Record<string, number> = {
  'Acheteur signataire admin': 3,
  'Prestataire signataire admin': 3,
  'Prestataire libre admin': 3,
  'Acheteur signataire': 2,
  'Prestataire signataire': 2,
  'Acheteur': 1,
  'Prestataire': 1,
  'Prestataire libre': 1,
};

const parseLocalDate = (dateStr: string): number => {
  const parts = (dateStr || '').split('/');
  if (parts.length === 3) return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0])).getTime();
  return 0;
};

const sortMembersByRoleThenDate = (users: any[]): any[] =>
  [...users].sort((a, b) => {
    const rankDiff = (ROLE_DISPLAY_RANK[b.role] ?? 1) - (ROLE_DISPLAY_RANK[a.role] ?? 1);
    if (rankDiff !== 0) return rankDiff;
    return parseLocalDate(b.dateMiseAJour) - parseLocalDate(a.dateMiseAJour);
  });

// Fonction utilitaire pour filtrer les doublons d'utilisateurs
// Garde uniquement le rôle le plus élevé pour chaque id_user
// Priorité : Acheteur signataire admin > Acheteur signataire > Acheteur
// IMPORTANT: Pour les prestataires avec lots, on garde toutes les lignes (une par lot/département)
const filterDuplicateUsers = (users: any[]): any[] => {
  const rolePriority: { [key: string]: number } = {
    'Acheteur signataire admin': 3,
    'Acheteur signataire': 2,
    'Acheteur': 1,
    'Prestataire signataire admin': 3,
    'Prestataire signataire': 2,
    'Prestataire': 1,
  };

  // Grouper les utilisateurs par clé composite (idUser + lot technique + département + contrat)
  // Cela permet de garder plusieurs lignes pour le même utilisateur s'il a des lots différents
  const usersByKey = new Map<string, any[]>();

  users.forEach(user => {
    // Pour les prestataires avec des lots, créer une clé composite unique
    // qui inclut l'id utilisateur + les infos de lot
    let key: string;
    if (user.lotTechniqueId !== undefined && user.lotDepartementId !== undefined) {
      // Clé composite pour les prestataires avec lots : garder chaque combinaison unique
      key = `${user.idUser || user.id}-${user.lotTechniqueId}-${user.lotDepartementId}-${user.contratNumero || ''}`;
    } else if (user.idUser) {
      // Pour les utilisateurs sans lots (acheteurs), grouper par idUser
      key = `user-${user.idUser}`;
    } else {
      // Si pas d'idUser, utiliser l'id
      key = `id-${user.id}`;
    }

    const existing = usersByKey.get(key) || [];
    existing.push(user);
    usersByKey.set(key, existing);
  });

  // Pour chaque groupe, garder uniquement celui avec le rôle le plus élevé
  const filteredUsers: any[] = [];

  usersByKey.forEach((userGroup) => {
    if (userGroup.length === 1) {
      filteredUsers.push(userGroup[0]);
    } else {
      // Trier par priorité de rôle et garder le plus élevé
      const sorted = userGroup.sort((a, b) => {
        const priorityA = rolePriority[a.role] || 0;
        const priorityB = rolePriority[b.role] || 0;
        return priorityB - priorityA;
      });
      filteredUsers.push(sorted[0]);
    }
  });

  return filteredUsers;
};

interface Column {
  field: string;
  header: string;
  renderCell: (value: any, row: any) => React.ReactNode;
}

interface GestionComptesProps {
  userType?: 'acheteur' | 'prestataire';  // Type d'utilisateur pour filtrer les données
}

const GestionComptes: React.FC<GestionComptesProps> = ({ userType = 'acheteur' }) => {
  const { user } = useAuth();
  const [isChangeRoleModalOpen, setIsChangeRoleModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [validatorsCount, setValidatorsCount] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [tempSelectedRole, setTempSelectedRole] = useState<string | null>(null);
  const [userData, setUserData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // États pour les filtres (prestataires)
  const [lotTechniqueFilter, setLotTechniqueFilter] = useState<string>('');
  const [lotDepartementFilter, setLotDepartementFilter] = useState<string>('');
  const [contratFilter, setContratFilter] = useState<string>('');
  const [availableLotsTechniques, setAvailableLotsTechniques] = useState<any[]>([]);
  const [availableLotsDepartements, setAvailableLotsDepartements] = useState<any[]>([]);
  const [availableContrats, setAvailableContrats] = useState<string[]>([]);

  // État pour la modal de réattribution
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
  const [selectedLotContrat, setSelectedLotContrat] = useState<any>(null);
  const [availableInterlocuteurs, setAvailableInterlocuteurs] = useState<any[]>([]);
  const [newInterlocuteurId, setNewInterlocuteurId] = useState<string>('');
  const [isReassigning, setIsReassigning] = useState(false);

  // Déclencheur de re-fetch des données (incrémenté pour déclencher le useEffect)
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // États pour la modal de passation (limite 3 gestionnaires)
  const [isPassationModalOpen, setIsPassationModalOpen] = useState(false);
  const [gestionnairesActifs, setGestionnairesActifs] = useState<any[]>([]);
  const [pendingPromotion, setPendingPromotion] = useState<any>(null);
  // EPIC-010/T06: États pour la modal de renvoi d'invitation (contexte acheteur)
  const [isResendInvitationModalOpen, setIsResendInvitationModalOpen] = useState(false);
  const [userForResendInvitation, setUserForResendInvitation] = useState<any>(null);

  // STORY-DEL-05: Suppression membre
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<DeleteMemberInfo | null>(null);

  // FRONT-16: États pour la modale d'attribution de membre à un lot (Prestataires Libres)
  // Logique inversée: On sélectionne un LOT dans la grille, puis on choisit un MEMBRE dans le modal
  const [isLotAssignModalOpen, setIsLotAssignModalOpen] = useState(false);

  // Modal de succès après création de compte
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [createdUserEmail, setCreatedUserEmail] = useState('');
  const [selectedLotForMember, setSelectedLotForMember] = useState<any>(null); // Le lot sélectionné
  const [selectedMemberId, setSelectedMemberId] = useState<string>(''); // Le membre à assigner
  const [availableMembersForLot, setAvailableMembersForLot] = useState<any[]>([]); // Liste des membres disponibles
  const [isAssigningLot, setIsAssigningLot] = useState(false);
  const [entiteLotsTechniques, setEntiteLotsTechniques] = useState<any[]>([]);
  const [entiteLotsDepartements, setEntiteLotsDepartements] = useState<any[]>([]);

  // Utiliser isClientRole pour déterminer le type d'utilisateur
  const userRole = user?.role || '';

  // Vérifier si l'utilisateur est un client ou un prestataire
  const isClient = isClientRole(userRole);
  const isPrestataireLibre = isPrestataireLibreRole(userRole);
  const isPrestataire = isPrestataireRole(userRole) && !isPrestataireLibre; // Exclure les prestataires libres
  const isAdmin = isAdminRole(userRole);

  // Body scroll lock when any inline modal is open
  const anyInlineModalOpen = isResetPasswordModalOpen || isChangeRoleModalOpen;
  useEffect(() => {
    document.body.style.overflow = anyInlineModalOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [anyInlineModalOpen]);

  // Définition dynamique des tabs selon le rôle
  const tabs = isClient
    ? ['Tous', 'Acheteur signataire admin', 'Acheteur signataire', 'Acheteur']
    : ['Tous', 'Prestataire signataire admin', 'Prestataire signataire', 'Prestataire'];

  // Charger les données des utilisateurs depuis l'API
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error("Token d'authentification non trouvé");

        // Transformer les données des organismes/entités en une liste d'utilisateurs
        const users: any[] = [];

        if (isPrestataireLibre) {
          // ============================================
          // FRONT-16: Traitement pour les PRESTATAIRES LIBRES
          // Affiche les LOTS de l'entité avec le membre assigné à chaque lot
          // L'admin PL peut modifier le MEMBRE assigné à un lot via un dropdown
          // (Même logique que pour les AC)
          // ============================================
          console.log('GestionComptes - Mode Prestataire Libre');

          // Récupérer le profil prestataire libre
          const profileResponse = await fetch(`${process.env.REACT_APP_API_URL}/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (!profileResponse.ok) {
            throw new Error('Erreur lors de la récupération du profil');
          }

          const profileData = await profileResponse.json();
          if (profileData.status !== 'success' || !profileData.data) {
            throw new Error('Format de réponse API inattendu');
          }

          const profile = profileData.data;
          const entite = profile.entite;

          // FRONT-16: Récupérer les lots de l'entité PL
          const lotsDepartementsLibre = profile.prestataire_libre?.lots_departements || [];

          // Récupérer les membres de l'équipe via /profile/team
          const teamResponse = await fetch(`${process.env.REACT_APP_API_URL}/profile/team`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          let membres: any[] = [];
          if (teamResponse.ok) {
            const teamData = await teamResponse.json();
            if (teamData.status === 'success' && teamData.data?.membres) {
              membres = teamData.data.membres;
            }
          }

          // Stocker les membres pour le dropdown dans la modale
          setAvailableMembersForLot(membres.map(m => ({
            id: m.id,
            nom: `${m.prenom} ${m.nom}`,
            email: m.email,
            role: m.role
          })));

          // FRONT-16: Créer un index des attributions par lot
          // Structure: { "lotTechniqueId_departementId": { membre, lotAssignmentId } }
          const assignmentsByLot: { [key: string]: any } = {};
          for (const membre of membres) {
            const lotsAttribues = membre.lots_attribues || [];
            for (const lotAttribue of lotsAttribues) {
              const key = `${lotAttribue.lot_technique_id}_${lotAttribue.departement_id}`;
              assignmentsByLot[key] = {
                membre: membre,
                lotAssignmentId: lotAttribue.id
              };
            }
          }

          // FRONT-16: Afficher CHAQUE LOT de l'entité avec le membre assigné
          for (const lotDept of lotsDepartementsLibre) {
            const key = `${lotDept.lot_technique_id}_${lotDept.departement_id}`;
            const assignment = assignmentsByLot[key];

            users.push({
              id: `lot_${lotDept.lot_technique_id}_${lotDept.departement_id}`, // ID unique pour la ligne
              // Info du lot (lecture seule)
              lotTechnique: {
                id: lotDept.lot_technique_id,
                nom: lotDept.lot_technique_nom || ''
              },
              lotDepartement: {
                id: lotDept.departement_id,
                nom: lotDept.departement_nom || ''
              },
              lotTechniqueId: lotDept.lot_technique_id,
              lotDepartementId: lotDept.departement_id,
              contratNumero: '', // PL n'a pas de contrats
              type: assignment ? 'Référencé' : 'Non attribué',
              // Info du membre assigné (ou vide)
              nom: assignment ? `${assignment.membre.prenom} ${assignment.membre.nom}` : '-',
              email: assignment ? assignment.membre.email : '',
              telephone: assignment ? (assignment.membre.telephone || '') : '',
              role: assignment ? (convertApiRoleToUiRole(assignment.membre.role) || 'Prestataire') : '-',
              dateMiseAJour: assignment
                ? new Date(assignment.membre.updated_at || assignment.membre.created_at).toLocaleDateString('fr-FR')
                : '-',
              // IDs pour la modification
              assignedMemberId: assignment ? assignment.membre.id : null,
              lotAssignmentId: assignment ? assignment.lotAssignmentId : null,
              entiteId: entite?.id,
              isPrestataireLibreLot: true // Flag pour identifier les lignes de lots PL
            });
          }

          // FRONT-16: Extraire les lots uniques pour les filtres
          const uniqueLotsTechniques = new Map();
          const uniqueLotsDepartements = new Map();

          lotsDepartementsLibre.forEach((ld: any) => {
            if (ld.lot_technique_id) {
              uniqueLotsTechniques.set(ld.lot_technique_id, {
                id: ld.lot_technique_id,
                nom: ld.lot_technique_nom || ''
              });
            }
            if (ld.departement_id) {
              uniqueLotsDepartements.set(ld.departement_id, {
                id: ld.departement_id,
                nom: ld.departement_nom || ''
              });
            }
          });

          // Stocker les lots de l'entité
          setEntiteLotsTechniques(Array.from(uniqueLotsTechniques.values()));
          setEntiteLotsDepartements(Array.from(uniqueLotsDepartements.values()));

          // Pour les filtres
          setAvailableLotsTechniques(Array.from(uniqueLotsTechniques.values()));
          setAvailableLotsDepartements(Array.from(uniqueLotsDepartements.values()));
          setAvailableContrats([]); // PL n'a pas de contrats

          setUserData(sortMembersByRoleThenDate(users));
          setFilteredData(sortMembersByRoleThenDate(users));
          setLoading(false);
          return; // IMPORTANT: Sortir ici pour les PL

        } else if (isPrestataire) {
          // ============================================
          // Traitement pour les prestataires ACCORD CADRE uniquement
          // Utilise /profile pour obtenir l'entité de l'utilisateur connecté
          // puis /interlocuteurs-by-lots pour les lots AC
          // ============================================
          console.log('GestionComptes - Mode Prestataire Accord Cadre');

          // Récupérer le profil pour obtenir l'entité
          const profileResponse = await fetch(`${process.env.REACT_APP_API_URL}/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          let entiteId: number | null = null;
          let entiteNom = '';

          if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            if (profileData.status === 'success' && profileData.data?.entite) {
              entiteId = profileData.data.entite.id;
              entiteNom = profileData.data.entite.nom || '';
            }
          }

          // Si pas d'entité via profile, fallback sur /entites (une seule entité)
          if (!entiteId) {
            const entitesResponse = await fetch(`${process.env.REACT_APP_API_URL}/entites`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              }
            });

            if (entitesResponse.ok) {
              const entitesData = await entitesResponse.json();
              if (entitesData.status === 'success' && entitesData.data?.length > 0) {
                // Prendre la première entité (celle de l'utilisateur)
                entiteId = entitesData.data[0].id;
                entiteNom = entitesData.data[0].nom || '';
              }
            }
          }

          if (!entiteId) {
            throw new Error('Aucune entité trouvée pour cet utilisateur');
          }

          // Récupérer les lots de cette entité (un seul appel)
          const lotsResponse = await fetch(`${process.env.REACT_APP_API_URL}/entites/${entiteId}/interlocuteurs-by-lots`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          let lots: any[] = [];
          let lotsSansInterlocuteur: any[] = [];
          if (lotsResponse.ok) {
            const lotsData = await lotsResponse.json();
            if (lotsData.status === 'success' && lotsData.data?.lots) {
              lots = lotsData.data.lots;
            }
            if (lotsData.status === 'success' && lotsData.data?.lots_sans_interlocuteur) {
              lotsSansInterlocuteur = lotsData.data.lots_sans_interlocuteur;
            }
          }

          // Créer les lignes pour chaque interlocuteur par lot
          for (const lot of lots) {
            for (const interlocuteur of lot.interlocuteurs || []) {
              const rang = interlocuteur.rang || 0;
              users.push({
                id: interlocuteur.id,
                idUser: interlocuteur.id_user,
                nom: `${interlocuteur.prenom} ${interlocuteur.nom}`,
                prenom: interlocuteur.prenom,
                email: interlocuteur.email_contact,
                role: convertApiRoleToUiRole((interlocuteur.fonction || 'interlocuteur').toLowerCase()),
                fonction: interlocuteur.fonction || 'Interlocuteur',
                telephone: interlocuteur.telephone || '',
                organisme: entiteNom,
                dateMiseAJour: new Date(interlocuteur.updated_at || interlocuteur.created_at).toLocaleDateString('fr-FR'),
                lotTechnique: lot.lot_technique,
                lotDepartement: lot.departement,
                lotTechniqueId: lot.lot_technique?.id,
                lotDepartementId: lot.departement?.id,
                contratNumero: interlocuteur.contrat_numero || '',
                entiteId: entiteId,
                type: rang > 0 ? `Titulaire rang ${rang}` : 'Référencé',
                rang: rang
              });
            }
          }

          // Lots contrat sans interlocuteur affecte : une ligne par lot orphelin, affectable.
          for (const lot of lotsSansInterlocuteur) {
            users.push({
              id: `orphan-${lot.lot_contrat_id}`,
              idUser: null,
              nom: '',
              prenom: '',
              email: '',
              role: '',
              fonction: '',
              telephone: '',
              organisme: entiteNom,
              dateMiseAJour: lot.updated_at ? new Date(lot.updated_at).toLocaleDateString('fr-FR') : '',
              lotTechnique: lot.lot_technique,
              lotDepartement: lot.departement,
              lotTechniqueId: lot.lot_technique?.id,
              lotDepartementId: lot.departement?.id,
              contratNumero: lot.contrat_numero || '',
              entiteId: entiteId,
              type: 'Non affecté',
              rang: lot.rang || 0,
              isOrphanLot: true,
              lotContratId: lot.lot_contrat_id
            });
          }

          // Extraire pour les filtres
          const uniqueLotsTechniques = new Map();
          const uniqueLotsDepartements = new Map();
          const uniqueContrats = new Set<string>();

          lots.forEach((lot: any) => {
            if (lot.lot_technique?.id) {
              uniqueLotsTechniques.set(lot.lot_technique.id, lot.lot_technique);
            }
            if (lot.departement?.id) {
              uniqueLotsDepartements.set(lot.departement.id, lot.departement);
            }
            for (const interlocuteur of lot.interlocuteurs || []) {
              if (interlocuteur.contrat_numero) {
                uniqueContrats.add(interlocuteur.contrat_numero);
              }
            }
          });

          lotsSansInterlocuteur.forEach((lot: any) => {
            if (lot.lot_technique?.id) uniqueLotsTechniques.set(lot.lot_technique.id, lot.lot_technique);
            if (lot.departement?.id) uniqueLotsDepartements.set(lot.departement.id, lot.departement);
            if (lot.contrat_numero) uniqueContrats.add(lot.contrat_numero);
          });

          setAvailableLotsTechniques(Array.from(uniqueLotsTechniques.values()));
          setAvailableLotsDepartements(Array.from(uniqueLotsDepartements.values()));
          setAvailableContrats(Array.from(uniqueContrats));

        } else {
          // Traitement pour les acheteurs (organismes)
          // Récupérer d'abord l'ID de l'organisme de l'utilisateur connecté
          const userEndpoint = userRole === 'Acheteur signataire admin'
            ? 'responsables-achat/me'
            : userRole === 'Acheteur signataire'
              ? 'validateurs-achat/me'
              : 'acheteurs/me';

          const meResponse = await fetch(`${process.env.REACT_APP_API_URL}/${userEndpoint}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (!meResponse.ok) {
            throw new Error('Erreur lors de la récupération des informations utilisateur');
          }

          const meData = await meResponse.json();
          const organismeId = meData.data?.id_organisme;

          if (!organismeId) {
            throw new Error('ID d\'organisme non trouvé');
          }

          // Récupérer les informations de l'organisme avec tous ses utilisateurs
          const organismeResponse = await fetch(`${process.env.REACT_APP_API_URL}/organismes/${organismeId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (!organismeResponse.ok) {
            throw new Error('Erreur lors de la récupération de l\'organisme');
          }

          const organismeData = await organismeResponse.json();

          if (organismeData.status !== 'success' || !organismeData.data) {
            throw new Error('Format de réponse API inattendu');
          }

          const organisme = organismeData.data;

          // Ajouter les responsables achat (Acheteur signataire admin)
          if (organisme.responsables_achat && organisme.responsables_achat.length > 0) {
            organisme.responsables_achat.forEach((responsable: any) => {
              users.push({
                id: responsable.id,
                idUser: responsable.id_user,
                nom: `${responsable.prenom} ${responsable.nom}`,
                prenom: responsable.prenom,
                email: responsable.email_contact,
                loginEmail: responsable.user?.email || '',
                role: 'Acheteur signataire admin',
                fonction: responsable.fonction || '',
                telephone: responsable.telephone || '',
                organisme: organisme.nom,
                nombreCommande: 0,
                valeurCommande: 0,
                dateMiseAJour: new Date(responsable.updated_at || responsable.created_at).toLocaleDateString('fr-FR')
              });
            });
          }

          // Ajouter les validateurs achat (Acheteur signataire)
          if (organisme.validateurs_achat && organisme.validateurs_achat.length > 0) {
            organisme.validateurs_achat.forEach((validateur: any) => {
              users.push({
                id: validateur.id,
                idUser: validateur.id_user,
                nom: `${validateur.prenom} ${validateur.nom}`,
                prenom: validateur.prenom,
                email: validateur.email_contact,
                loginEmail: validateur.user?.email || '',
                role: 'Acheteur signataire',
                fonction: validateur.fonction || '',
                telephone: validateur.telephone || '',
                organisme: organisme.nom,
                nombreCommande: 0,
                valeurCommande: 0,
                dateMiseAJour: new Date(validateur.updated_at || validateur.created_at).toLocaleDateString('fr-FR')
              });
            });
          }

          // Ajouter les acheteurs (Acheteur)
          if (organisme.acheteurs && organisme.acheteurs.length > 0) {
            organisme.acheteurs.forEach((acheteur: any) => {
              users.push({
                id: acheteur.id,
                idUser: acheteur.id_user,
                nom: `${acheteur.prenom} ${acheteur.nom}`,
                prenom: acheteur.prenom,
                email: acheteur.email_contact,
                loginEmail: acheteur.user?.email || '',
                role: 'Acheteur',
                fonction: acheteur.fonction || '',
                telephone: acheteur.telephone || '',
                organisme: organisme.nom,
                nombreCommande: 0,
                valeurCommande: 0,
                dateMiseAJour: new Date(acheteur.updated_at || acheteur.created_at).toLocaleDateString('fr-FR')
              });
            });
          }
        }

        // Filtrer les doublons (garder uniquement le rôle le plus élevé pour chaque utilisateur)
        const uniqueUsers = filterDuplicateUsers(users);

        // Trier : Admins > Signataires > Autres, puis par date de modification décroissante
        const sortedUsers = sortMembersByRoleThenDate(uniqueUsers);

        setUserData(sortedUsers);
        setFilteredData(sortedUsers);

        // Pour les prestataires (AC et libres), extraire les lots techniques, départements et contrats disponibles
        if (isPrestataire || isPrestataireLibre) {
          const lotsTechniquesSet = new Set();
          const lotsDepartementsSet = new Set();
          const contratsSet = new Set();

          sortedUsers.forEach((user: any) => {
            if (user.lotTechnique) {
              lotsTechniquesSet.add(JSON.stringify({ id: user.lotTechniqueId, nom: user.lotTechnique.nom }));
            }
            if (user.lotDepartement) {
              lotsDepartementsSet.add(JSON.stringify({ id: user.lotDepartementId, nom: user.lotDepartement.nom }));
            }
            if (user.contratNumero) {
              contratsSet.add(user.contratNumero);
            }
          });

          setAvailableLotsTechniques(Array.from(lotsTechniquesSet).map((item: any) => JSON.parse(item)));
          setAvailableLotsDepartements(Array.from(lotsDepartementsSet).map((item: any) => JSON.parse(item)));
          setAvailableContrats(Array.from(contratsSet) as string[]);
        }

        // Compter les validateurs
        const validatorsCount = users.filter(user => user.role === 'Acheteur signataire').length;
        setValidatorsCount(validatorsCount);

      } catch (error) {
        console.error('Erreur lors de la récupération des utilisateurs:', error);
        setError(error instanceof Error ? error.message : 'Une erreur est survenue');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [isPrestataire, isPrestataireLibre, refreshTrigger]);

  // Appliquer les filtres pour les prestataires
  useEffect(() => {
    if (!isPrestataire && !isPrestataireLibre) return;

    let filtered = [...userData];

    // Filtre par lot technique
    if (lotTechniqueFilter) {
      filtered = filtered.filter(user =>
        user.lotTechniqueId === parseInt(lotTechniqueFilter)
      );
    }

    // Filtre par lot département
    if (lotDepartementFilter) {
      filtered = filtered.filter(user =>
        user.lotDepartementId === parseInt(lotDepartementFilter)
      );
    }

    // Filtre par N° Contrat
    if (contratFilter) {
      filtered = filtered.filter(user =>
        user.contratNumero === contratFilter
      );
    }

    setFilteredData(filtered);
  }, [lotTechniqueFilter, lotDepartementFilter, contratFilter, userData, isPrestataire]);

  // Fonction pour récupérer les gestionnaires actifs depuis userData
  const getGestionnairesActifs = () => {
    if (isPrestataire || isPrestataireLibre) {
      // Filtrer les utilisateurs avec le rôle "Prestataire signataire admin" (gestionnaire)
      return userData.filter(u => u.role === 'Prestataire signataire admin');
    } else {
      // Filtrer les utilisateurs avec le rôle "Acheteur signataire admin" (gestionnaire)
      return userData.filter(u => u.role === 'Acheteur signataire admin');
    }
  };

  // Fonction pour ouvrir le modal de changement de rôle
  const handleRoleClick = (userRow: any) => {
    if (!isAdmin) return;
    setSelectedUser(userRow);
    setIsChangeRoleModalOpen(true);
  };

  // Fonction pour gérer le changement de rôle
  const handleChangeRole = async (newRole: string) => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      const roleForApi = convertUiRoleToApiRole(newRole);
      const typeCompteForApi = convertUiRoleToApiRole(selectedUser.role);

      let teamPrefix = 'acheteur-profile';
      if (userRole === 'Prestataire signataire admin') teamPrefix = 'gestionnaires';
      else if (isPrestataireLibreRole(userRole)) teamPrefix = 'profile';

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${teamPrefix}/team/${selectedUser.id}/change-role`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            new_role: roleForApi,
            type_compte: typeCompteForApi
          })
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || `Erreur API: ${response.status}`);
      }

      const updatedData = userData.map(u => {
        if (u.id === selectedUser.id) {
          return { ...u, role: newRole };
        }
        return u;
      });

      setUserData(updatedData);
      setFilteredData(updatedData);
      setIsChangeRoleModalOpen(false);
      setTempSelectedRole(null);
    } catch (error: any) {
      console.error('Error updating role:', error);
      alert(error.message || "Une erreur est survenue lors du changement de rôle.");
    }
  };

  // Fonction pour confirmer la passation de gestionnaire
  const handleConfirmPassation = async (gestionnaireSortantId: number) => {
    if (!pendingPromotion) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error("Token d'authentification non trouvé");

      // Appeler l'API pour initier la passation
      const response = await fetch(`${process.env.REACT_APP_API_URL}/gestionnaires/initiate-passation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          nouveau_gestionnaire_id: pendingPromotion.user.idUser,
          gestionnaire_sortant_id: gestionnaireSortantId,
          nouveau_role: convertUiRoleToApiRole(pendingPromotion.newRole)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de l\'initiation de la passation');
      }

      const data = await response.json();

      // Afficher un message de succès
      alert(`Un email de validation a été envoyé au gestionnaire sortant. La passation sera effective après validation.`);

      // Fermer le modal et réinitialiser les états
      setIsPassationModalOpen(false);
      setPendingPromotion(null);
      setGestionnairesActifs([]);

      // Re-fetcher les données sans rechargement complet de la page
      setRefreshTrigger(t => t + 1);

    } catch (error: any) {
      console.error('Erreur lors de la passation:', error);
      alert(error.message || "Une erreur est survenue lors de l'initiation de la passation.");
    }
  };

  // EPIC-010/T06: Ouvrir la modal de confirmation de renvoi d'invitation
  const handleResendInvitation = (user: any) => {
    setUserForResendInvitation(user);
    setIsResendInvitationModalOpen(true);
  };

  // EPIC-010/T06: Appeler POST /equipe/membres/renvoyer/{userId}
  const handleConfirmResendInvitation = async () => {
    if (!userForResendInvitation) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error("Token d'authentification non trouvé");

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/equipe/membres/renvoyer/${userForResendInvitation.idUser}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        }
      );
      const data = await response.json();
      if (!response.ok || data.status !== 'success') {
        throw new Error(data.message || "Erreur lors du renvoi de l'invitation");
      }
      setIsResendInvitationModalOpen(false);
      setUserForResendInvitation(null);
      alert(`L'invitation a été renvoyée à ${userForResendInvitation.email}.`);
    } catch (error: any) {
      console.error('Erreur renvoi invitation:', error);
      alert(error.message || "Une erreur est survenue lors du renvoi de l'invitation.");
    }
  };

  const handleSearch = (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setFilteredData(userData);
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    const filtered = userData.filter(item =>
      item.nom.toLowerCase().includes(searchTermLower) ||
      item.email.toLowerCase().includes(searchTermLower) ||
      (item.organisme || '').toLowerCase().includes(searchTermLower)
    );

    setFilteredData(filtered);
  };

  const handleExport = () => {

    // Exemple: Création d'un CSV basique
    const headers = ["Nom", "Email", "Rôle", "Fonction", "Organisme", "Date de mise à jour"];
    const csvData = [
      headers.join(","),
      ...filteredData.map(user => [
        `"${user.nom}"`,
        `"${user.email}"`,
        `"${user.role}"`,
        `"${user.fonction || ''}"`,
        `"${user.organisme || ''}"`,
        `"${user.dateMiseAJour || ''}"`,
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'utilisateurs.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFilter = () => {
  };

  const handleCreateCompte = async (formData: any): Promise<{ ok: boolean; errors?: Record<string, string>; message?: string }> => {
    const token = localStorage.getItem('token');
    if (!token) return { ok: false, message: "Token d'authentification non trouvé" };

    const flattenErrors = (errorsObj: Record<string, any>): Record<string, string> => {
      const flat: Record<string, string> = {};
      Object.entries(errorsObj).forEach(([key, msgs]) => {
        if (Array.isArray(msgs) && (msgs as string[]).length > 0) flat[key] = (msgs as string[])[0];
      });
      return flat;
    };

    try {
      if (formData.role === 'prestataire_libre_admin') {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/team`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            nom: formData.nom,
            prenom: formData.prenom,
            email: formData.email.toLowerCase(),
            telephone: formData.telephone,
            fonction: formData.fonction || '',
          }),
        });
        const data = await response.json();
        if (response.status === 422 && data.errors) return { ok: false, errors: flattenErrors(data.errors) };
        if (!response.ok || data.status !== 'success') return { ok: false, message: data.message || 'Erreur création membre PL' };

        const newMemberId = data.data?.id;
        if (newMemberId) {
          await fetch(`${process.env.REACT_APP_API_URL}/profile/team/${newMemberId}/change-role`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ new_role: 'Prestataire_Libre_Admin', type_compte: 'Prestataire_Libre' }),
          });
        }
        setRefreshTrigger(t => t + 1);

      } else if (formData.role === 'gestionnaire') {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/gestionnaires/team`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            nom: formData.nom,
            prenom: formData.prenom,
            email: formData.email.toLowerCase(),
            telephone: formData.telephone || null,
            fonction: formData.fonction || null,
          }),
        });
        const data = await response.json();
        if (response.status === 422 && data.errors) return { ok: false, errors: flattenErrors(data.errors) };
        if (!response.ok || data.status !== 'success') return { ok: false, message: data.message || 'Erreur création gestionnaire' };
        setRefreshTrigger(t => t + 1);

      } else {
        if (formData.role === 'acheteur_signataire') {
          const currentSignataires = userData.filter(u => u.role === 'Acheteur signataire').length;
          if (currentSignataires >= 3) {
            return { ok: false, message: 'Vous ne pouvez pas avoir plus de 3 signataires par organisme.' };
          }
        }

        const roleMapping: Record<string, string> = {
          acheteur: 'acheteur',
          acheteur_signataire: 'validateur',
          responsable_achat: 'responsable_achat',
        };
        const apiRole = roleMapping[formData.role] ?? formData.role;

        const response = await fetch(`${process.env.REACT_APP_API_URL}/equipe/membres`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            nom: formData.nom,
            prenom: formData.prenom,
            email: formData.email.toLowerCase(),
            telephone: formData.telephone || null,
            role: apiRole,
            fonction: formData.fonction || '',
          }),
        });
        const data = await response.json();
        if (response.status === 422 && data.errors) return { ok: false, errors: flattenErrors(data.errors) };
        if (!response.ok || data.status !== 'success') return { ok: false, message: data.message || 'Erreur lors de la création du compte' };
        setRefreshTrigger(t => t + 1);
      }

      setCreatedUserEmail(formData.email.toLowerCase());
      setIsSuccessModalOpen(true);
      return { ok: true };

    } catch (error) {
      return { ok: false, message: error instanceof Error ? error.message : 'Une erreur est survenue' };
    }
  };


  const handleEditUser = (userId: string | any) => {
    // Si c'est un objet (row), extraire l'id
    const actualUserId = typeof userId === 'object' ? userId.id : userId;
    console.log(`Modifier les informations de l'utilisateur ${actualUserId}`);
    const user = userData.find(u => u.id === actualUserId);
    setSelectedUser(user);
    setIsEditUserModalOpen(true);
  };

  const handleSaveUser = async (userId: string, updatedUserData: { nom: string; prenom: string; email: string; telephone: string; fonction: string; role?: string }) => {
    try {
      if (!selectedUser) return false;

      const token = localStorage.getItem('token');

      let teamPrefix = 'acheteur-profile';
      if (userRole === 'Prestataire signataire admin') teamPrefix = 'gestionnaires';
      else if (isPrestataireLibreRole(userRole)) teamPrefix = 'profile';

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${teamPrefix}/team/${selectedUser.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            nom: updatedUserData.nom,
            prenom: updatedUserData.prenom,
            email_contact: updatedUserData.email.toLowerCase(),
            telephone: updatedUserData.telephone,
            fonction: updatedUserData.fonction
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erreur API: ${response.status}`);
      }

      let updatedList = userData.map(u => {
        if (u.id === selectedUser.id) {
          return {
            ...u,
            nom: updatedUserData.prenom + ' ' + updatedUserData.nom,
            prenom: updatedUserData.prenom,
            email: updatedUserData.email.toLowerCase(),
            telephone: updatedUserData.telephone,
            fonction: updatedUserData.fonction,
            dateMiseAJour: new Date().toLocaleDateString('fr-FR')
          };
        }
        return u;
      });

      if (updatedUserData.role && updatedUserData.role !== selectedUser.role) {
        const roleForApi = convertUiRoleToApiRole(updatedUserData.role);
        const typeCompteForApi = convertUiRoleToApiRole(selectedUser.role);
        const roleResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/${teamPrefix}/team/${selectedUser.id}/change-role`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
            body: JSON.stringify({ new_role: roleForApi, type_compte: typeCompteForApi })
          }
        );
        if (!roleResponse.ok) {
          const roleErr = await roleResponse.json().catch(() => ({}));
          throw new Error(roleErr.message || 'Erreur lors du changement de rôle');
        }
        const roleData = await roleResponse.json();
        const newId = roleData.new_id ?? selectedUser.id;
        updatedList = updatedList.map(u =>
          u.id === selectedUser.id ? { ...u, id: newId, role: updatedUserData.role! } : u
        );
        setSelectedUser((prev: any) => prev ? { ...prev, id: newId, role: updatedUserData.role! } : prev);
      }

      const sortedList = sortMembersByRoleThenDate(updatedList);
      setUserData(sortedList);
      setFilteredData(sortedList);
      setIsEditUserModalOpen(false);
      return true;
    } catch (error: any) {
      console.error('Erreur handleSaveUser:', error);
      throw error;
    }
  };

  const handleSaveLoginEmail = async (newEmail: string, memberId: number): Promise<void> => {
    const token = localStorage.getItem('token');

    let teamPrefix = 'acheteur-profile';
    if (userRole === 'Prestataire signataire admin') teamPrefix = 'gestionnaires';
    else if (isPrestataireLibreRole(userRole)) teamPrefix = 'profile';

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/${teamPrefix}/team/${memberId}/email`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email: newEmail.toLowerCase() })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Erreur lors de la mise à jour de l'email de connexion.`);
    }

    // Update loginEmail in local state
    setUserData(prev => prev.map(u => Number(u.id) === memberId ? { ...u, loginEmail: newEmail.toLowerCase() } : u));
    setFilteredData(prev => prev.map(u => Number(u.id) === memberId ? { ...u, loginEmail: newEmail.toLowerCase() } : u));
  };

  const handleResetPassword = (userId: string | any) => {
    // Si c'est un objet (row), extraire l'id
    const actualUserId = typeof userId === 'object' ? userId.id : userId;
    console.log(`Réinitialiser le mot de passe pour l'utilisateur ${actualUserId}`);
    setSelectedUser(userData.find(user => user.id === actualUserId));
    setIsResetPasswordModalOpen(true);
  };

  // Fonction pour confirmer la réinitialisation du mot de passe
  const handleConfirmResetPassword = async () => {
    if (!selectedUser) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email: selectedUser.email.toLowerCase() })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la réinitialisation du mot de passe');
      }

      alert(`Un email de réinitialisation a été envoyé à ${selectedUser.email}`);
      setIsResetPasswordModalOpen(false);

    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Une erreur est survenue'}`);
    }
  };

  // Fonction pour ouvrir la modal de réattribution
  const handleOpenReassignModal = async (row: any) => {
    setSelectedLotContrat(row);
    setNewInterlocuteurId(''); // Réinitialiser la sélection

    // Récupérer tous les interlocuteurs de l'entité
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/entites/${row.entiteId}/all-interlocuteurs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Interlocuteurs récupérés:', data);
        if (data.status === 'success' && data.data) {
          setAvailableInterlocuteurs(data.data);
        }
      } else {
        console.error('Erreur HTTP:', response.status);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des interlocuteurs:', error);
    }

    setIsReassignModalOpen(true);
  };

  // Fonction pour réattribuer un interlocuteur à un lot_contrat
  const handleReassignInterlocuteur = async () => {
    if (!selectedLotContrat || !newInterlocuteurId) {
      alert('Veuillez sélectionner un interlocuteur');
      return;
    }

    // Empêcher les appels multiples
    if (isReassigning) {
      return;
    }

    setIsReassigning(true);

    try {
      const token = localStorage.getItem('token');

      // Lot orphelin : le lot_contrat_id est deja porte par la ligne, on affecte directement
      // (l'endpoint reassign exige un interlocuteur courant, qui n'existe pas ici).
      if (selectedLotContrat.isOrphanLot && selectedLotContrat.lotContratId) {
        const affectResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/interlocuteurs/lot-contrat/${selectedLotContrat.lotContratId}/affecter`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ id_interlocuteur: newInterlocuteurId })
          }
        );

        if (affectResponse.ok) {
          alert('Lot affecté avec succès');
          setIsReassignModalOpen(false);
          setNewInterlocuteurId('');
          localStorage.setItem('activeProfilTab', 'Gestion d\'équipe');
          window.location.reload();
        } else {
          const errorData = await affectResponse.json();
          throw new Error(errorData.message || 'Erreur lors de l\'affectation');
        }
        return;
      }

      // Trouver le lot_contrat_id à partir des informations du row
      // Pour cela, il faut utiliser l'API /entites/{id}/interlocuteurs-by-lots
      // et chercher le lot_contrat qui correspond au lot technique + département + contrat

      const entiteResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/entites/${selectedLotContrat.entiteId}/interlocuteurs-by-lots`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );

      if (!entiteResponse.ok) {
        throw new Error('Erreur lors de la récupération des lots-contrats');
      }

      const entiteData = await entiteResponse.json();

      // Trouver le lot_contrat_id correspondant
      let lotContratId = null;
      if (entiteData.status === 'success' && entiteData.data?.lots) {
        const matchingLot = entiteData.data.lots.find((lot: any) => {
          return lot.lot_technique.id === selectedLotContrat.lotTechniqueId &&
            lot.departement.id === selectedLotContrat.lotDepartementId;
        });

        if (matchingLot?.interlocuteurs) {
          const matchingInterlocuteur = matchingLot.interlocuteurs.find((inter: any) =>
            inter.id === parseInt(selectedLotContrat.id)
          );
          lotContratId = matchingInterlocuteur?.lot_contrat_id;
        }
      }

      if (!lotContratId) {
        throw new Error('Impossible de trouver le lot_contrat_id');
      }

      // Appel API pour mettre à jour le lot_contrat
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/interlocuteurs/${selectedLotContrat.id}/reassign`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id_interlocuteur: newInterlocuteurId,
            id_lot_contrat: lotContratId
          })
        }
      );

      if (response.ok) {
        alert('Interlocuteur réattribué avec succès');
        setIsReassignModalOpen(false);
        setNewInterlocuteurId('');
        // Stocker l'onglet "Gestion d'équipe" pour y revenir après le rechargement
        localStorage.setItem('activeProfilTab', 'Gestion d\'équipe');
        // Recharger les données
        window.location.reload();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de la réattribution');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur lors de la réattribution: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
      setIsReassigning(false);
    }
  };

  // ============================================
  // FRONT-16: Fonctions pour l'attribution de MEMBRE à un LOT (Prestataires Libres)
  // Logique inversée: La grille affiche les LOTS, le modal permet de choisir un MEMBRE
  // ============================================

  // Ouvrir la modale d'attribution de membre pour un lot PL
  const handleOpenLotAssignModal = (row: any) => {
    setSelectedLotForMember(row);
    // Pré-sélectionner le membre actuel si le lot est déjà attribué
    setSelectedMemberId(row.assignedMemberId?.toString() || '');
    setIsLotAssignModalOpen(true);
  };

  // Sauvegarder l'attribution de membre à un lot
  const handleSaveLotAssignment = async () => {
    if (!selectedLotForMember || !selectedMemberId) {
      alert('Veuillez sélectionner un membre');
      return;
    }

    setIsAssigningLot(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/profile/team/${selectedMemberId}/lots`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            lot_technique_id: selectedLotForMember.lotTechniqueId,
            lot_departement_id: selectedLotForMember.lotDepartementId
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        alert(data.message || 'Membre assigné avec succès');
        setIsLotAssignModalOpen(false);
        // Stocker l'onglet "Gestion d'équipe" pour y revenir après le rechargement
        localStorage.setItem('activeProfilTab', 'Gestion d\'équipe');
        // Recharger les données
        window.location.reload();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de l\'attribution');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur lors de l'attribution: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setIsAssigningLot(false);
    }
  };

  // Retirer l'attribution d'un membre d'un lot
  const handleRemoveLotAssignment = async () => {
    if (!selectedLotForMember || !selectedLotForMember.lotAssignmentId) {
      alert('Aucune attribution à retirer');
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir retirer l\'attribution de ce lot ?')) {
      return;
    }

    setIsAssigningLot(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/profile/team/${selectedLotForMember.assignedMemberId}/lots/${selectedLotForMember.lotAssignmentId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );

      if (response.ok) {
        alert('Attribution retirée avec succès');
        setIsLotAssignModalOpen(false);
        localStorage.setItem('activeProfilTab', 'Gestion d\'équipe');
        window.location.reload();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors du retrait');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur lors du retrait: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setIsAssigningLot(false);
    }
  };

  // Type API d'une ligne membre acheteur (acheteur/validateur/responsable_achat)
  // depuis son role UI. Retourne undefined hors de ce perimetre : le serveur
  // retombe alors sur sa resolution historique.
  const typeCompteAcheteur = (uiRole: string): string | undefined => {
    const api = convertUiRoleToApiRole(uiRole || '');
    return ['acheteur', 'validateur', 'responsable_achat'].includes(api) ? api : undefined;
  };

  // Fonction pour supprimer un utilisateur — ouvre DeleteMemberModal
  const handleOpenDeleteModal = (member: any) => {
    const prenom = member.prenom || '';
    // member.nom est stocké comme "${prenom} ${nom}" dans userData — extraire uniquement le nom de famille
    const nomFamille = prenom && member.nom && member.nom.startsWith(prenom + ' ')
      ? member.nom.slice(prenom.length + 1)
      : member.nom || '';
    setMemberToDelete({
      id: Number(member.id),
      nom: nomFamille,
      prenom: prenom,
      email: member.email || '',
      // Le type leve l'ambiguite d'id entre tables cote serveur
      typeCompte: typeCompteAcheteur(member.role || ''),
    });
    setIsDeleteModalOpen(true);
  };

  const handleDeleteSuccess = (memberId: number) => {
    setIsDeleteModalOpen(false);
    setMemberToDelete(null);
    setUserData((prev) => prev.filter((m) => Number(m.id) !== memberId));
    setFilteredData((prev) => prev.filter((m) => Number(m.id) !== memberId));
  };

  // Fonction pour supprimer un utilisateur (garde legacy pour prestataires via menu)
  const handleDeleteUser = (userId: string | any) => {
    // Si c'est un objet (row), extraire l'id
    const actualUserId = typeof userId === 'object' ? userId.id : userId;

    // Confirmer la suppression
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) {
      console.log(`Supprimer l'utilisateur ${actualUserId}`);
      // Implémentation de la suppression via API
      const token = localStorage.getItem('token');
      // Trouver l'utilisateur pour déterminer son rôle et la bonne route API
      const userToDelete = userData.find(user => user.id === actualUserId);
      if (!userToDelete) return;

      // Déterminer la bonne route API en fonction du rôle de l'utilisateur à supprimer
      let apiRoute = '';
      switch (userToDelete.role) {
        case 'Prestataire':
          apiRoute = `${process.env.REACT_APP_API_URL}/interlocuteurs/${actualUserId}`;
          break;
        case 'Prestataire signataire':
          apiRoute = `${process.env.REACT_APP_API_URL}/signataires/${actualUserId}`;
          break;
        case 'Acheteur':
          apiRoute = `${process.env.REACT_APP_API_URL}/acheteurs/${actualUserId}`;
          break;
        case 'Acheteur signataire':
          apiRoute = `${process.env.REACT_APP_API_URL}/validateurs-achat/${actualUserId}`;
          break;
        default:
          alert("Type d'utilisateur non pris en charge pour la suppression");
          return;
      }

      // Appel API pour supprimer l'utilisateur
      fetch(apiRoute, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
        .then(response => {
          if (!response.ok) throw new Error('Erreur lors de la suppression');
          // Mettre à jour la liste d'utilisateurs localement
          const updatedData = userData.filter(user => user.id !== actualUserId);
          setUserData(updatedData);
          setFilteredData(updatedData);
          alert("Utilisateur supprimé avec succès");
        })
        .catch(error => {
          console.error('Erreur:', error);
          alert("Erreur lors de la suppression de l'utilisateur");
        });
    }
  };

  // Fonction qui renvoie l'icône appropriée pour les actions de suppression
  const getDeleteIcon = () => {
    return <CrossIcon />;
  };

  // Définir les options d'action pour chaque ligne en fonction du rôle de l'utilisateur
  const getActionOptions = (row: any) => {
    const userId = row.id;
    const rowRole = row.role;

    // Si l'utilisateur connecté est un prestataire admin (Gestionnaire)
    if (userRole === 'Prestataire signataire admin') {
      // Les options disponibles dépendent du rôle de l'utilisateur dans la ligne
      if (rowRole === 'Prestataire signataire admin') {
        // Un gestionnaire ne peut pas modifier un autre gestionnaire
        return [];
      } else if (rowRole === 'Prestataire signataire') {
        // Pour un signataire, on peut modifier, réinitialiser et supprimer
        return [
          {
            label: 'Modifier les informations',
            onClick: () => handleEditUser(userId),
            icon: <UserEditIcon />
          },
          {
            label: 'Réinitialiser le mot de passe',
            onClick: () => handleResetPassword(userId),
            icon: <KeyIcon />
          },
          {
            label: 'Supprimer',
            onClick: () => handleDeleteUser(userId),
            icon: <CrossIcon />
          }
        ];
      } else if (rowRole === 'Prestataire') {
        // Pour un interlocuteur, on peut modifier, promouvoir, réinitialiser et supprimer
        return [
          {
            label: 'Modifier les informations',
            onClick: () => handleEditUser(userId),
            icon: <UserEditIcon />
          },
          {
            label: 'Promouvoir',
            onClick: () => handleRoleClick(row),
            icon: <EditIcon />
          },
          {
            label: 'Réinitialiser le mot de passe',
            onClick: () => handleResetPassword(userId),
            icon: <KeyIcon />
          },
          {
            label: 'Supprimer',
            onClick: () => handleDeleteUser(userId),
            icon: <CrossIcon />
          }
        ];
      }
    }
    // Si l'utilisateur connecté est un prestataire signataire
    else if (userRole === 'Prestataire signataire') {
      // Un signataire peut uniquement supprimer des interlocuteurs
      if (rowRole === 'Prestataire') {
        return [
          {
            label: 'Supprimer',
            onClick: () => handleDeleteUser(userId),
            icon: <CrossIcon />
          }
        ];
      }
      return [];
    }

    // Pour les autres rôles (acheteurs), comportement par défaut
    return [
      {
        label: 'Modifier les informations',
        onClick: () => handleEditUser(userId),
        icon: <UserEditIcon />
      },
      {
        label: 'Réinitialiser le mot de passe',
        onClick: () => handleResetPassword(userId),
        icon: <KeyIcon />
      }
    ];
  };

  const columns = [
    // Colonnes Lots Techniques et Départements (uniquement pour prestataires)
    ...((isPrestataire || isPrestataireLibre) ? [
      {
        field: 'lotTechnique',
        header: 'Lot Technique',
        renderCell: (value: any) => {
          if (!value) {
            return <div className="no-lot">-</div>;
          }
          const shortName = value.nom.substring(0, 8);
          return (
            <div className="lot-technique" title={`${value.id} - ${value.nom}`}>
              {value.id} - {shortName}
            </div>
          );
        }
      },
      {
        field: 'lotDepartement',
        header: 'Département',
        renderCell: (value: any) => {
          if (!value) {
            return <div className="no-lot">-</div>;
          }
          const shortName = value.nom.substring(0, 8);
          return (
            <div className="lot-departement" title={`${String(value.id).padStart(2, '0')} - ${value.nom}`}>
              {String(value.id).padStart(2, '0')} - {shortName}
            </div>
          );
        }
      },
      // FRONT-16: Colonne N° Contrat uniquement pour AC (pas pour PL)
      ...(!isPrestataireLibre ? [{
        field: 'contratNumero',
        header: 'N° Contrat',
        renderCell: (value: string) => {
          if (!value) {
            return <div className="no-lot">-</div>;
          }
          return (
            <div className="contrat-numero">
              {value}
            </div>
          );
        }
      }] : []),
      {
        field: 'type',
        header: 'Type',
        renderCell: (value: string) => (
          <div className="type-cell">
            {value || '-'}
          </div>
        )
      }
    ] : []),
    {
      field: 'nom',
      header: 'Nom / Prénom',
      renderCell: (value: string) => (
        <div className="user-name">
          {value}
        </div>
      )
    },
    {
      field: 'role',
      header: 'Rôle',
      renderCell: (value: string, row: any) => {
        if (!row) {
          return <div className="user-role">{value}</div>;
        }

        // Pour les prestataires, désactiver COMPLÈTEMENT la modification des rôles
        // Aucun prestataire (même admin) ne peut modifier les rôles
        const isRoleEditable = false; // Toujours false pour les prestataires

        return (
          <div
            className="user-role"
            style={{
              cursor: 'default',
              textDecoration: 'none',
              opacity: 0.8
            }}
          >
            <span>{value}</span>
          </div>
        );
      }
    },
    {
      field: 'email',
      header: 'Email',
      renderCell: (value: string) => (
        <div className="user-email">
          {formatEmail(value)}
        </div>
      )
    },
    {
      field: 'telephone',
      header: 'Téléphone',
      renderCell: (value: string) => (
        <div className="user-phone">
          {formatPhone(value)}
        </div>
      )
    },
    ...((isPrestataire || isPrestataireLibre || isClient) ? [
      {
        field: 'fonction',
        header: 'Fonction',
        renderCell: (value: string) => (
          <div className="user-fonction">
            {value || '-'}
          </div>
        )
      }
    ] : []),
    {
      field: 'dateMiseAJour',
      header: 'Date de mise à jour',
      renderCell: (value: string) => (
        <div className="creation-date">
          {value}
        </div>
      )
    },
    // Colonne Actions pour les prestataires et les acheteurs admins
    ...((isPrestataire || isPrestataireLibre || (isClient && isAdminRole(userRole))) ? [
      {
        field: 'actions',
        header: 'Actions',
        renderCell: (value: any, row: any) => {
          // FRONT-16: Pour les Prestataires Libres : bouton pour modifier le membre assigné au lot
          if (isPrestataireLibre) {
            // Seul le Prestataire Libre Admin peut modifier les affectations
            const canModify = isAdmin;

            if (canModify) {
              return (
                <div className="actions-cell">
                  <Button
                    value="Modifier"
                    variant="secondary"
                    size="small"
                    icon={<EditIcon />}
                    onClick={() => handleOpenLotAssignModal(row)}
                  />
                </div>
              );
            } else {
              return (
                <div className="actions-cell" style={{ color: '#999', fontStyle: 'italic' }}>
                  -
                </div>
              );
            }
          }

          // Pour les prestataires AC : bouton "Modifier" pour réattribution (seulement pour admin)
          if (isPrestataire) {
            // Seul le Prestataire signataire admin peut modifier les affectations
            const canModify = userRole === 'Prestataire signataire admin';

            if (canModify) {
              return (
                <div className="actions-cell">
                  <Button
                    value={row.isOrphanLot ? "Affecter" : "Modifier"}
                    variant="secondary"
                    size="small"
                    icon={<EditIcon />}
                    onClick={() => handleOpenReassignModal(row)}
                  />
                </div>
              );
            } else {
              return (
                <div className="actions-cell" style={{ color: '#999', fontStyle: 'italic' }}>
                  Non autorisé
                </div>
              );
            }
          }

          // Pour les acheteurs admins : boutons Modifier/MDP/Renvoyer pour les acheteurs simples
          if (isClient && isAdminRole(userRole)) {
            // Afficher les actions pour tous les membres
            return (
              <div className="actions-cell" style={{ display: 'flex', gap: '0.5rem' }}>
                <Button
                  value="Modifier"
                  variant="secondary"
                  size="small"
                  icon={<EditIcon />}
                  onClick={() => handleEditUser(row)}
                />
                <Button
                  value="MDP"
                  variant="secondary"
                  size="small"
                  icon={<KeyIcon />}
                  onClick={() => handleResetPassword(row)}
                />
                <Button
                  value="Renvoyer"
                  variant="secondary"
                  size="small"
                  onClick={() => handleResendInvitation(row)}
                />
              </div>
            );
          }

          return null;
        }
      }
    ] : [])
  ];

  if (loading && !(isClient && (ACHETEURS_VIEW_MODE as string) !== 'table')) {
    return <div className="loading-indicator">Chargement des utilisateurs...</div>;
  }

  if (error) {
    return <div className="error-message">Erreur: {error}</div>;
  }

  return (
    <div className="gestion-comptes">
      <div className="gestion-comptes-header">
        {/* Pour les prestataires : afficher les filtres */}
        {(isPrestataire || isPrestataireLibre) && (
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', width: '100%', marginBottom: '1rem' }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label htmlFor="lot-technique-filter" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Lot Technique
              </label>
              <select
                id="lot-technique-filter"
                value={lotTechniqueFilter}
                onChange={(e) => setLotTechniqueFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              >
                <option value="">Tous les lots techniques</option>
                {availableLotsTechniques.map((lot: any) => (
                  <option key={lot.id} value={lot.id}>
                    {lot.id} - {lot.nom}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ flex: '1', minWidth: '200px' }}>
              <label htmlFor="lot-departement-filter" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Lot Département
              </label>
              <select
                id="lot-departement-filter"
                value={lotDepartementFilter}
                onChange={(e) => setLotDepartementFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              >
                <option value="">Tous les départements</option>
                {availableLotsDepartements.map((lot: any) => (
                  <option key={lot.id} value={lot.id}>
                    {String(lot.id).padStart(2, '0')} - {lot.nom}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ flex: '1', minWidth: '200px' }}>
              <label htmlFor="contrat-filter" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                N° Contrat
              </label>
              <select
                id="contrat-filter"
                value={contratFilter}
                onChange={(e) => setContratFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              >
                <option value="">Tous les contrats</option>
                {availableContrats.map((contrat: string) => (
                  <option key={contrat} value={contrat}>
                    {contrat}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Pour les acheteurs : afficher la barre de recherche et le bouton de création si admin */}
        {!isPrestataire && !isPrestataireLibre && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '1rem', gap: '1rem' }}>
            <div style={{ flex: '1' }}>
              <SearchBar
                placeholder="Rechercher un membre"
                onSearch={handleSearch}
              />
            </div>
            {isAdminRole(userRole) && (
              <Button
                value="Créer un utilisateur"
                variant="primary"
                size="small"
                icon={<PlusIcon />}
                onClick={() => setIsCreateModalOpen(true)}
                disabled={false}
              />
            )}
          </div>
        )}
      </div>
      {isClient && (ACHETEURS_VIEW_MODE as string) !== 'table' ? (
        loading ? (
          <div className="gc-membres-cards-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="gc-membre-card gc-membre-card-skeleton">
                <div className="gc-membre-card-inner">
                  <span className="sk sk-badge" />
                  <div className="gc-membre-card-header">
                    <div className="gc-membre-card-banner sk sk-banner" />
                    <div className="gc-membre-card-avatar-wrapper">
                      <div className="sk sk-avatar" />
                    </div>
                  </div>
                  <div className="gc-membre-card-body">
                    <div className="sk sk-line sk-line--name" />
                    <div className="sk sk-line sk-line--fonction" />
                    <div className="sk sk-line sk-line--contact" />
                    <div className="sk sk-line sk-line--contact" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredData.length === 0 ? (
          <p className="gc-membres-cards-empty">Aucun membre trouvé.</p>
        ) : (
          <div className="gc-membres-cards-grid">
            {filteredData.map((member) => {
              const prenomDisplay = member.prenom
                ? member.prenom.charAt(0).toUpperCase() + member.prenom.slice(1).toLowerCase()
                : '';
              const nomDisplay = (member.prenom
                ? member.nom.slice(member.prenom.length).trim()
                : member.nom).toUpperCase();
              const nameParts = member.nom.trim().split(' ');
              const initials = [
                member.prenom?.[0] || nameParts[0]?.[0],
                nameParts[nameParts.length - 1]?.[0]
              ].filter(Boolean).join('').toUpperCase() || '?';
              const isAdminMember = isAdminRole(member.role);
              const isEditable = isAdminRole(userRole);
              return (
                <div
                  key={member.id}
                  className={`gc-membre-card${isEditable ? ' gc-membre-card--editable' : ''}${(member.role || '').toLowerCase().includes('signataire') ? ' gc-membre-card--titulaire' : ' gc-membre-card--libre'}`}
                  onClick={() => { if (isEditable) handleEditUser(member); }}
                  role={isEditable ? 'button' : undefined}
                  tabIndex={isEditable ? 0 : undefined}
                  onKeyDown={isEditable ? (e) => { if (e.key === 'Enter') handleEditUser(member); } : undefined}
                >
                  <div className="gc-membre-card-inner">
                    <span className="gc-membre-card-role-badge">{member.role}</span>
                    <div className="gc-membre-card-header">
                      <div className="gc-membre-card-banner" />
                      <div className="gc-membre-card-avatar-wrapper">
                        <span className="gc-membre-card-avatar">{initials}</span>
                      </div>
                    </div>
                    <div className="gc-membre-card-body">
                      <p className="gc-membre-card-name">{prenomDisplay} {nomDisplay}</p>
                      {member.fonction && (
                        <p className="gc-membre-card-fonction">
                          {member.fonction.charAt(0).toUpperCase() + member.fonction.slice(1).toLowerCase()}
                        </p>
                      )}
                      <div className="gc-membre-card-contacts">
                        {member.email && <span className="gc-membre-card-contact gc-membre-card-email">{formatEmail(member.email)}</span>}
                        {member.telephone && <span className="gc-membre-card-contact">{formatPhone(member.telephone)}</span>}
                      </div>
                    </div>
                    <div className="gc-membre-card-footer">
                      {member.dateMiseAJour && (
                        <span className="gc-membre-card-date">Modifié le {member.dateMiseAJour}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <TableauDeBord
          data={filteredData}
          columns={columns}
          onSearch={handleSearch}
          onExport={handleExport}
          isSelectable={false}
          selectedItems={[]}
          onSelectionChange={() => { }}
          showActionsColumn={false}
          itemsPerPage={500}
        />
      )}

      {/* Modals */}
      {isAdminRole(userRole) && (
        <>
          {/* Modal de création de compte */}
          <CreationComptesModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            onCreate={handleCreateCompte}
          />

          {/* Modal de réinitialisation de mot de passe */}
          {isResetPasswordModalOpen && selectedUser && (
            <div className="modal-overlay" style={{ display: 'flex' }} onClick={() => setIsResetPasswordModalOpen(false)}>
              <div className="modal-container" style={{ maxWidth: '500px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Réinitialiser le mot de passe</h2>
                  <button onClick={() => setIsResetPasswordModalOpen(false)} className="close-button">
                    <CrossIcon />
                  </button>
                </div>
                <div className="modal-body">
                  <p>
                    Voulez-vous envoyer un email de réinitialisation de mot de passe à <strong>{selectedUser.email}</strong> ?
                  </p>
                </div>
                <div className="modal-actions">
                  <Button
                    value="Annuler"
                    variant="secondary"
                    size="small"
                    onClick={() => setIsResetPasswordModalOpen(false)}
                  />
                  <Button
                    value="Envoyer"
                    variant="primary"
                    size="small"
                    onClick={handleConfirmResetPassword}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Modal d'édition utilisateur */}
          {selectedUser && (
            <EditUserModal
              isOpen={isEditUserModalOpen}
              onClose={() => setIsEditUserModalOpen(false)}
              onSave={handleSaveUser}
              userId={selectedUser.id}
              userName={selectedUser.nom}
              userEmail={selectedUser.email}
              userPrenom={selectedUser.prenom}
              userFonction={selectedUser.fonction}
              userRole={selectedUser.role}
              userTelephone={selectedUser.telephone}
              userLoginEmail={selectedUser.loginEmail || ''}
              onSaveLoginEmail={handleSaveLoginEmail}
              availableRoles={
                Number(selectedUser.idUser) === Number(user?.id)
                  ? []
                  : isAdmin
                    ? isClient
                      ? [
                        { value: 'Acheteur', label: 'Acheteur' },
                        { value: 'Acheteur signataire', label: 'Acheteur signataire' },
                        { value: 'Acheteur signataire admin', label: 'Acheteur signataire admin' }
                      ]
                      : userRole === 'Prestataire signataire admin'
                        ? [
                          { value: 'Prestataire', label: 'Prestataire' },
                          { value: 'Prestataire signataire', label: 'Prestataire signataire' },
                          { value: 'Prestataire signataire admin', label: 'Prestataire signataire admin' }
                        ]
                        : isPrestataireLibre
                          ? [
                            { value: 'Prestataire libre', label: 'Prestataire libre' },
                            { value: 'Prestataire libre admin', label: 'Prestataire libre admin' }
                          ]
                          : []
                    : []
              }
              onDeleteRequest={
                Number(selectedUser.idUser) !== Number(user?.id)
                  ? () => { setIsEditUserModalOpen(false); handleOpenDeleteModal(selectedUser); }
                  : undefined
              }
              onResetPasswordRequest={() => {
                setIsEditUserModalOpen(false);
                handleResetPassword(selectedUser);
              }}
            />
          )}

          {/* Modal de changement de rôle */}
          {selectedUser && (
            <div className={`modal-overlay ${isChangeRoleModalOpen ? 'show' : 'hide'}`}
              style={{ display: isChangeRoleModalOpen ? 'flex' : 'none' }}
              onClick={() => { setIsChangeRoleModalOpen(false); setTempSelectedRole(null); }}>
              <div className="change-role-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Changer le rôle de {selectedUser.nom}</h2>
                  <button type="button" className="close-button" onClick={() => { setIsChangeRoleModalOpen(false); setTempSelectedRole(null); }}>
                    <CrossIcon />
                  </button>
                </div>
                <p>Rôle actuel: {selectedUser.role}</p>

                {/* État temporaire pour stocker le nouveau rôle sélectionné */}
                <div className="role-options">
                  {/* Afficher les options de rôle appropriées en fonction du type d'utilisateur */}
                  {isPrestataireLibreRole(userRole) ? (
                    // Options pour les Prestataires Libres
                    <>
                      <button
                        className={`role-button ${selectedUser.role === 'Prestataire libre'
                          ? 'active'
                          : tempSelectedRole === 'Prestataire libre'
                            ? 'selected'
                            : ''
                          }`}
                        onClick={() => setTempSelectedRole('Prestataire libre')}
                        disabled={selectedUser.role === 'Prestataire libre'}
                      >
                        Prestataire libre
                      </button>
                      <button
                        className={`role-button ${selectedUser.role === 'Prestataire libre admin'
                          ? 'active'
                          : tempSelectedRole === 'Prestataire libre admin'
                            ? 'selected'
                            : ''
                          }`}
                        onClick={() => setTempSelectedRole('Prestataire libre admin')}
                        disabled={selectedUser.role === 'Prestataire libre admin'}
                      >
                        Prestataire libre admin
                      </button>
                    </>
                  ) : isPrestataireRole(selectedUser.role) ? (
                    // Options pour les prestataires AC (gestionnaire/interlocuteur)
                    <>
                      <button
                        className={`role-button ${selectedUser.role === 'Prestataire'
                          ? 'active'
                          : tempSelectedRole === 'Prestataire'
                            ? 'selected'
                            : ''
                          }`}
                        onClick={() => setTempSelectedRole('Prestataire')}
                        disabled={selectedUser.role === 'Prestataire'}
                      >
                        Prestataire
                      </button>
                      <button
                        className={`role-button ${selectedUser.role === 'Prestataire signataire admin'
                          ? 'active'
                          : tempSelectedRole === 'Prestataire signataire admin'
                            ? 'selected'
                            : ''
                          }`}
                        onClick={() => setTempSelectedRole('Prestataire signataire admin')}
                        disabled={selectedUser.role === 'Prestataire signataire admin'}
                      >
                        Prestataire signataire admin
                      </button>
                    </>
                  ) : (
                    // Options pour les acheteurs
                    <>
                      <button
                        className={`role-button ${selectedUser.role === 'Acheteur'
                          ? 'active'
                          : tempSelectedRole === 'Acheteur'
                            ? 'selected'
                            : ''
                          }`}
                        onClick={() => setTempSelectedRole('Acheteur')}
                        disabled={selectedUser.role === 'Acheteur'}
                      >
                        Acheteur
                      </button>
                      <button
                        className={`role-button ${selectedUser.role === 'Acheteur signataire'
                          ? 'active'
                          : tempSelectedRole === 'Acheteur signataire'
                            ? 'selected'
                            : ''
                          }`}
                        onClick={() => setTempSelectedRole('Acheteur signataire')}
                        disabled={selectedUser.role === 'Acheteur signataire'}
                      >
                        Acheteur signataire
                      </button>
                      <button
                        className={`role-button ${selectedUser.role === 'Acheteur signataire admin'
                          ? 'active'
                          : tempSelectedRole === 'Acheteur signataire admin'
                            ? 'selected'
                            : ''
                          }`}
                        onClick={() => setTempSelectedRole('Acheteur signataire admin')}
                        disabled={selectedUser.role === 'Acheteur signataire admin'}
                      >
                        Acheteur signataire admin
                      </button>
                    </>
                  )}
                </div>

                <div className="modal-actions">
                  <Button
                    variant="secondary"
                    size="small"
                    value="Annuler"
                    onClick={() => {
                      setIsChangeRoleModalOpen(false);
                      setTempSelectedRole(null);
                    }}
                  />
                  <Button
                    variant="primary"
                    size="small"
                    value="Valider"
                    disabled={!tempSelectedRole || tempSelectedRole === selectedUser.role}
                    onClick={() => {
                      if (tempSelectedRole) {
                        handleChangeRole(tempSelectedRole);
                        setTempSelectedRole(null);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal de réattribution d'interlocuteur (Prestataires AC et Libres) */}
      {(isPrestataire || isPrestataireLibre) && selectedLotContrat && (
        <div className={`modal-overlay ${isReassignModalOpen ? 'show' : 'hide'}`}
          style={{ display: isReassignModalOpen ? 'flex' : 'none' }}>
          <div className="change-role-modal">
            <div className="modal-header">
              <h2>{selectedLotContrat.isOrphanLot ? 'Affecter un interlocuteur' : 'Réattribuer un interlocuteur'}</h2>
              <button
                onClick={() => setIsReassignModalOpen(false)}
                className="close-button"
              >
                <CrossIcon />
              </button>
            </div>

            <div className="modal-body">
              <div className="info-section">
                <div className="info-row">
                  <span className="info-label">Lot Technique:</span>
                  <span className="info-value">{selectedLotContrat.lotTechnique?.nom}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Département:</span>
                  <span className="info-value">{selectedLotContrat.lotDepartement?.nom}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">N° Contrat:</span>
                  <span className="info-value">{selectedLotContrat.contratNumero}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Interlocuteur actuel:</span>
                  <span className="info-value">{selectedLotContrat.isOrphanLot ? 'Aucun (lot non affecté)' : selectedLotContrat.nom}</span>
                </div>
              </div>

              <div className="form-group-modal">
                <label htmlFor="new-interlocuteur">
                  Nouvel interlocuteur :
                </label>
                <select
                  id="new-interlocuteur"
                  className="form-select"
                  value={newInterlocuteurId}
                  onChange={(e) => setNewInterlocuteurId(e.target.value)}
                >
                  <option value="">-- Sélectionner un interlocuteur --</option>
                  {availableInterlocuteurs.map((interlocuteur: any) => (
                    <option key={interlocuteur.id} value={interlocuteur.id}>
                      {interlocuteur.prenom} {interlocuteur.nom} - {interlocuteur.type} ({interlocuteur.email_contact})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="modal-actions">
              <Button
                variant="secondary"
                size="small"
                value="Annuler"
                onClick={() => {
                  setIsReassignModalOpen(false);
                  setNewInterlocuteurId('');
                }}
              />
              <Button
                variant="primary"
                size="small"
                value={isReassigning ? "En cours..." : "Valider"}
                disabled={!newInterlocuteurId || isReassigning}
                onClick={handleReassignInterlocuteur}
              />
            </div>
          </div>
        </div>
      )}

      {/* FRONT-16: Modal d'attribution de MEMBRE à un LOT (Prestataires Libres) */}
      {isPrestataireLibre && selectedLotForMember && (
        <div className={`modal-overlay ${isLotAssignModalOpen ? 'show' : 'hide'}`}
          style={{ display: isLotAssignModalOpen ? 'flex' : 'none' }}>
          <div className="change-role-modal lot-assign-modal">
            <div className="modal-header">
              <h2>Attribuer un membre au lot</h2>
              <button
                onClick={() => setIsLotAssignModalOpen(false)}
                className="close-button"
              >
                <CrossIcon />
              </button>
            </div>

            <div className="modal-body">
              {/* Informations du lot en lecture seule */}
              <div className="info-section" style={{ marginBottom: '1.5rem' }}>
                <div className="info-row">
                  <span className="info-label">Lot Technique :</span>
                  <span className="info-value" style={{ fontWeight: 'bold' }}>
                    {selectedLotForMember.lotTechnique?.id} - {selectedLotForMember.lotTechnique?.nom}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Département :</span>
                  <span className="info-value" style={{ fontWeight: 'bold' }}>
                    {String(selectedLotForMember.lotDepartement?.id || '').padStart(2, '0')} - {selectedLotForMember.lotDepartement?.nom}
                  </span>
                </div>
                {selectedLotForMember.assignedMemberId && (
                  <div className="info-row" style={{ marginTop: '0.5rem', color: '#666' }}>
                    <span className="info-label">Actuellement attribué à :</span>
                    <span className="info-value">{selectedLotForMember.nom}</span>
                  </div>
                )}
              </div>

              {/* Dropdown pour sélectionner le membre */}
              <div className="form-group-modal">
                <label htmlFor="member-select">
                  Membre à assigner <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  id="member-select"
                  className="form-select"
                  value={selectedMemberId}
                  onChange={(e) => setSelectedMemberId(e.target.value)}
                >
                  <option value="">-- Sélectionner un membre --</option>
                  {availableMembersForLot.map((membre: any) => (
                    <option key={membre.id} value={membre.id}>
                      {membre.nom} ({membre.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="modal-actions">
              {/* Bouton pour retirer l'attribution si le lot est déjà attribué */}
              {selectedLotForMember.lotAssignmentId && (
                <Button
                  variant="secondary"
                  size="small"
                  value="Retirer"
                  onClick={handleRemoveLotAssignment}
                  disabled={isAssigningLot}
                />
              )}
              <Button
                variant="secondary"
                size="small"
                value="Annuler"
                onClick={() => {
                  setIsLotAssignModalOpen(false);
                  setSelectedMemberId('');
                }}
              />
              <Button
                variant="primary"
                size="small"
                value={isAssigningLot ? "En cours..." : "Enregistrer"}
                disabled={!selectedMemberId || isAssigningLot}
                onClick={handleSaveLotAssignment}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de passation de gestionnaire */}
      <PassationModal
        isOpen={isPassationModalOpen}
        onClose={() => {
          setIsPassationModalOpen(false);
          setPendingPromotion(null);
          setGestionnairesActifs([]);
        }}
        onConfirm={handleConfirmPassation}
        gestionnairesActifs={gestionnairesActifs}
        nouveauGestionnaire={pendingPromotion?.user || null}
      />

      {/* STORY-DEL-05: Modal de suppression membre */}
      {isDeleteModalOpen && memberToDelete && (
        <DeleteMemberModal
          member={memberToDelete}
          availableMembers={userData
            .filter((m) => !(Number(m.id) === memberToDelete.id && typeCompteAcheteur(m.role || '') === memberToDelete.typeCompte))
            .map((m) => {
              const prenom = m.prenom || '';
              const nomFamille = prenom && m.nom && m.nom.startsWith(prenom + ' ')
                ? m.nom.slice(prenom.length + 1)
                : m.nom || '';
              return {
                id: Number(m.id),
                nom: nomFamille,
                prenom,
                email: m.email || '',
                role: m.role || '',
                typeCompte: typeCompteAcheteur(m.role || ''),
              };
            })
          }
          dependenciesEndpoint={`${process.env.REACT_APP_API_URL}/acheteur-profile/team/${memberToDelete.id}/dependencies`}
          transferEndpoint={`${process.env.REACT_APP_API_URL}/acheteur-profile/team/${memberToDelete.id}/transfer`}
          deleteEndpoint={`${process.env.REACT_APP_API_URL}/acheteur-profile/team/${memberToDelete.id}`}
          onSuccess={handleDeleteSuccess}
          onClose={() => { setIsDeleteModalOpen(false); setMemberToDelete(null); }}
        />
      )}

      {/* EPIC-010/T06: Modal de confirmation de renvoi d'invitation */}
      {isResendInvitationModalOpen && userForResendInvitation && (
        <div className="modal-overlay" style={{ display: 'flex' }} onClick={() => setIsResendInvitationModalOpen(false)}>
          <div className="modal-content" style={{ maxWidth: '480px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Renvoyer l'invitation</h2>
            </div>
            <div className="modal-body">
              <p style={{ lineHeight: '1.8', fontSize: '1.4rem', color: '#333' }}>
                Souhaitez-vous renvoyer un lien d'invitation à{' '}
                <strong>{userForResendInvitation.nom}</strong>{' '}
                (<em>{userForResendInvitation.email}</em>) ?
              </p>
            </div>
            <div className="modal-actions">
              <Button
                value="Annuler"
                variant="secondary"
                onClick={() => { setIsResendInvitationModalOpen(false); setUserForResendInvitation(null); }}
              />
              <Button
                value="Renvoyer"
                variant="primary"
                onClick={handleConfirmResendInvitation}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de succès après création de compte */}
      {isSuccessModalOpen && (
        <div className="modal-overlay" style={{ display: 'flex' }}>
          <div className="modal-content" style={{ maxWidth: '500px', width: '90%' }}>
            <div className="modal-header">
              <h2>Compte créé avec succès</h2>
            </div>
            <div className="modal-body">
              <p style={{ lineHeight: '1.8', fontSize: '1.5rem', color: '#333' }}>
                Un email a été envoyé à <strong>{createdUserEmail}</strong> avec un lien pour définir son mot de passe de connexion à Centralis.
              </p>
            </div>
            <div className="modal-actions">
              <Button
                value="Compris"
                variant="primary"
                onClick={() => setIsSuccessModalOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionComptes;